import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { HeartHandshake, Phone, Search, CalendarDays, ChevronDown, ChevronUp, Loader2, FileText, CalendarRange, ChevronRight, ArrowLeft, X, Package, Clock, MapPin } from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/Card";
import { InlineRowsSkeleton } from "../components/ui/Skeleton";
import { Input, Select, Label } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { useI18n } from "../contexts/I18nContext";
import { formatVND, timeAgo } from "../lib/utils";
import { cn } from "../lib/utils";
import { useSort } from "../hooks/useSort";

const stripAccents = (s) => { if (!s) return ''; return String(s).toLowerCase().replace(/đ/g, 'd').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); };

export default function CustomerCare() {
  const { t, lang } = useI18n();

  const STATUS_LABELS = {
    new: "Mới",
    processing: "Đang xử lý",
    delivering: "Đang giao",
    delivered: "Đã giao",
    debt_pending: "Còn nợ",
    cancelled: "Đã huỷ",
  };
  const tStatus = (status) => t(`status.${status}`) || STATUS_LABELS[status] || status;
  const [days, setDays] = useState(0);  
  const [q, setQ] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [showNeedsCareOnly, setShowNeedsCareOnly] = useState(false);
  const [data, setData] = useState(null);

  // --- STATE CHO TÍNH NĂNG XỔ ĐƠN HÀNG VÀ LỌC THỜI GIAN ---
  const [timeFilter, setTimeFilter] = useState("today"); // today, week, month, year, custom, all
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [periodStats, setPeriodStats] = useState({}); // {customer_id: {orders, spent}}

  const load = async () => {
    const { data } = await api.get(`/customers/care?days=${days}`);
    setData(data);
  };

  const loadPeriodStats = async (filter, customFrom, customTo) => {
    const { date_from, date_to } = (() => {
      if (filter === "all") return { date_from: null, date_to: null };
      if (filter === "custom") return { date_from: customFrom, date_to: customTo };
      const now = new Date();
      const vnNow = new Date(now.getTime() + 7 * 60 * 60 * 1000);
      const y = vnNow.getUTCFullYear(), m = vnNow.getUTCMonth(), d = vnNow.getUTCDate(), dow = vnNow.getUTCDay();
      const fmt = (dt) => `${dt.getUTCFullYear()}-${String(dt.getUTCMonth()+1).padStart(2,"0")}-${String(dt.getUTCDate()).padStart(2,"0")}`;
      if (filter === "today") return { date_from: fmt(new Date(Date.UTC(y,m,d))), date_to: fmt(new Date(Date.UTC(y,m,d))) };
      if (filter === "week") { const diff = dow===0?-6:1-dow; return { date_from: fmt(new Date(Date.UTC(y,m,d+diff))), date_to: fmt(new Date(Date.UTC(y,m,d+diff+6))) }; }
      if (filter === "month") return { date_from: fmt(new Date(Date.UTC(y,m,1))), date_to: fmt(new Date(Date.UTC(y,m+1,0))) };
      if (filter === "year") return { date_from: fmt(new Date(Date.UTC(y,0,1))), date_to: fmt(new Date(Date.UTC(y,11,31))) };
      return { date_from: null, date_to: null };
    })();
    const params = {};
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    const { data } = await api.get("/customers/order-stats", { params });
    setPeriodStats(data || {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  useEffect(() => { loadPeriodStats(timeFilter, startDate, endDate); /* eslint-disable-next-line */ }, [timeFilter, startDate, endDate]);

  // --- HÀM TÍNH TOÁN NGÀY (UTC+7) ---
  const getDateRange = (filter) => {
    if (filter === "all") return { date_from: null, date_to: null };
    if (filter === "custom") return { date_from: startDate, date_to: endDate };

    // Tính ngày theo giờ VN (UTC+7) để tránh lệch ngày
    const now = new Date();
    const vnNow = new Date(now.getTime() + 7 * 60 * 60 * 1000);
    const y = vnNow.getUTCFullYear();
    const m = vnNow.getUTCMonth();
    const d = vnNow.getUTCDate();
    const dow = vnNow.getUTCDay(); // 0=CN

    const fmt = (dt) => {
      const yy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(dt.getUTCDate()).padStart(2, "0");
      return `${yy}-${mm}-${dd}`;
    };

    let start, end;

    if (filter === "today") {
      start = new Date(Date.UTC(y, m, d));
      end = new Date(Date.UTC(y, m, d));
    } else if (filter === "week") {
      const diffToMonday = dow === 0 ? -6 : 1 - dow;
      start = new Date(Date.UTC(y, m, d + diffToMonday));
      end = new Date(Date.UTC(y, m, d + diffToMonday + 6));
    } else if (filter === "month") {
      start = new Date(Date.UTC(y, m, 1));
      end = new Date(Date.UTC(y, m + 1, 0));
    } else if (filter === "year") {
      start = new Date(Date.UTC(y, 0, 1));
      end = new Date(Date.UTC(y, 11, 31));
    }

    return { date_from: fmt(start), date_to: fmt(end) };
  };

  // --- HÀM XỬ LÝ KHI BẤM XỔ XUỐNG ---
  const handleExpand = async (customerId) => {
    if (expandedId === customerId) {
      setExpandedId(null);
      return;
    }
    
    setExpandedId(customerId);
    setLoadingOrders(true);
    setExpandedOrders([]);

    const { date_from, date_to } = getDateRange(timeFilter);
    const params = { customer_id: customerId };
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;

    try {
      const res = await api.get("/orders", { params });
      setExpandedOrders(res.data || []);
    } catch (e) {
      console.error("Lỗi lấy danh sách đơn hàng:", e);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);
    setExpandedId(null); 
    if (filter !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  const filtered = (data?.items || []).filter((c) => {
    if (q) {
      const s = q.toLowerCase();
      if (!stripAccents(`${c.name || ""} ${c.phone || ""} ${c.nickname || ""}`).includes(stripAccents(s))) return false;
    }
    if (district && c.district !== district) return false;
    if (city && c.city !== city) return false;
    if (showNeedsCareOnly && !c.needs_care) return false;
    return true;
  });

  // Chuẩn bị data phẳng để useSort có thể sort trực tiếp theo field
  const flatFiltered = useMemo(() => filtered.map((c) => ({
    ...c,
    _code: c.code || c.customer_id || "",
    _area: `${c.city || ""}${c.district || ""}`,
    _orders: periodStats[c.customer_id]?.orders ?? -1,
  })), [filtered, periodStats]); // eslint-disable-line

  const { sorted, handleSort, SortIcon } = useSort(flatFiltered, "_code", "asc");

  const districts = Array.from(new Set((data?.items || []).map((c) => c.district).filter(Boolean)));
  const cities = Array.from(new Set((data?.items || []).map((c) => c.city).filter(Boolean)));

  const headerText = days === 0
    ? "Khách chưa đặt HÔM NAY hiển thị riêng. Khách đã đặt hôm nay lên đầu."
    : `Khách chưa đặt trong ${days}+ ngày sẽ hiển thị xuống dưới.`;

  // --- TÍNH TỔNG TRONG BẢNG XỔ XUỐNG ---
  const expandedTotalAmount = expandedOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  // --- STATE & HÀM XEM CHI TIẾT 1 ĐƠN HÀNG ---
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);

  const openOrderDetail = async (orderId, e) => {
    if (e) e.stopPropagation();
    if (!orderId) return;
    setOrderDetailLoading(true);
    setOrderDetail(null);
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrderDetail(data);
    } catch {
      // toast nếu có
    } finally {
      setOrderDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-care-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <HeartHandshake size={28} className="text-terracotta" /> {t("customers.careTitle")}
        </h1>
        <p className="text-sm text-ink-secondary mt-1">{headerText}</p>
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Tổng khách — xanh indigo */}
          <div className="relative overflow-hidden rounded-xl p-4 text-white shadow-md"
            style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10" />
            <div className="absolute -right-1 bottom-0 w-10 h-10 rounded-full bg-white/10" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-white/70">Tổng khách</span>
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center text-sm">👥</div>
              </div>
              <div className="font-heading text-3xl font-bold">{data.total}</div>
              <div className="text-[11px] text-white/60 mt-0.5">khách hàng</div>
            </div>
          </div>

          {/* Chưa đặt — cam amber */}
          <div className="relative overflow-hidden rounded-xl p-4 text-white shadow-md"
            style={{ background: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)" }}>
            <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10" />
            <div className="absolute -right-1 bottom-0 w-10 h-10 rounded-full bg-white/10" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-white/80">
                  {days === 0 ? "Chưa đặt hôm nay" : `Chưa đặt ${days}+ ngày`}
                </span>
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center text-sm">⏰</div>
              </div>
              <div className="font-heading text-3xl font-bold">{data.needs_care_count}</div>
              <div className="text-[11px] text-white/70 mt-0.5">cần chăm sóc</div>
            </div>
          </div>

          {/* Đã đặt — xanh lá */}
          <div className="relative overflow-hidden rounded-xl p-4 text-white shadow-md"
            style={{ background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)" }}>
            <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10" />
            <div className="absolute -right-1 bottom-0 w-10 h-10 rounded-full bg-white/10" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-white/80">
                  {days === 0 ? "Đã đặt hôm nay" : "Đã đặt gần đây"}
                </span>
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center text-sm">✅</div>
              </div>
              <div className="font-heading text-3xl font-bold">{data.total - data.needs_care_count}</div>
              <div className="text-[11px] text-white/70 mt-0.5">khách hoạt động</div>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
            <div className="sm:col-span-2">
              <Label>{t("common.search")}</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" placeholder="Tên / SĐT" data-testid="care-search" />
              </div>
            </div>
            <div>
              <Label>{t("common.district")}</Label>
              <Select value={district} onChange={(e) => setDistrict(e.target.value)} data-testid="care-district">
                <option value="">{t("common.all")}</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </Select>
            </div>
            <div>
              <Label>{t("common.city")}</Label>
              <Select value={city} onChange={(e) => setCity(e.target.value)} data-testid="care-city">
                <option value="">{t("common.all")}</option>
                {cities.map((d) => <option key={d} value={d}>{d}</option>)}
              </Select>
            </div>
            <div>
              <Label>Chưa đặt (Cảnh báo)</Label>
              <Select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="care-days">
                <option value={0}>Hôm nay</option>
                <option value={3}>3 ngày</option>
                <option value={7}>7 ngày</option>
                <option value={14}>14 ngày</option>
                <option value={30}>30 ngày</option>
                <option value={60}>60 ngày</option>
              </Select>
            </div>
            <div className="sm:col-span-6 flex items-end justify-between">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={showNeedsCareOnly} onChange={(e) => setShowNeedsCareOnly(e.target.checked)} data-testid="care-needs-only" />
                Chỉ KH cần chăm sóc
              </label>
              <span className="text-xs text-ink-muted italic">Bấm vào tiêu đề cột để sắp xếp</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KHU VỰC CHỌN THỜI GIAN XEM ĐƠN NHANH */}
      <div className="flex flex-wrap items-center gap-3 bg-cream/30 p-2 rounded-lg border border-border">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-secondary ml-2">Xem đơn hàng:</span>
          <Button size="sm" variant={timeFilter === "today" ? "default" : "outline"} onClick={() => handleTimeFilterChange("today")}>
            Hôm nay
          </Button>
          <Button size="sm" variant={timeFilter === "week" ? "default" : "outline"} onClick={() => handleTimeFilterChange("week")}>
            Tuần này
          </Button>
          <Button size="sm" variant={timeFilter === "month" ? "default" : "outline"} onClick={() => handleTimeFilterChange("month")}>
            Tháng này
          </Button>
          <Button size="sm" variant={timeFilter === "year" ? "default" : "outline"} onClick={() => handleTimeFilterChange("year")}>
            Năm nay
          </Button>
          <Button size="sm" variant={timeFilter === "all" ? "default" : "outline"} onClick={() => handleTimeFilterChange("all")}>
            Tất cả
          </Button>
        </div>
        
        {/* TÙY CHỌN NGÀY */}
        <div className="flex items-center gap-2 pl-2 sm:pl-4 sm:border-l border-border">
          <CalendarRange size={16} className="text-ink-muted hidden sm:block" />
          <Label className="text-xs">Từ:</Label>
          <Input 
            type="date" 
            className="h-8 text-xs w-auto px-2" 
            value={startDate} 
            onChange={(e) => { setStartDate(e.target.value); setTimeFilter("custom"); setExpandedId(null); }} 
          />
          <Label className="text-xs">Đến:</Label>
          <Input 
            type="date" 
            className="h-8 text-xs w-auto px-2" 
            value={endDate} 
            onChange={(e) => { setEndDate(e.target.value); setTimeFilter("custom"); setExpandedId(null); }} 
          />
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left py-3 px-4 w-10"></th>
                <th
                  className="text-left py-3 px-4 cursor-pointer hover:text-ink select-none whitespace-nowrap"
                  onClick={() => handleSort("_code")}
                >
                  Mã KH {SortIcon("_code")}
                </th>
                <th
                  className="text-left py-3 px-4 cursor-pointer hover:text-ink select-none"
                  onClick={() => handleSort("name")}
                >
                  Khách hàng {SortIcon("name")}
                </th>
                <th
                  className="text-left py-3 px-4 cursor-pointer hover:text-ink select-none"
                  onClick={() => handleSort("phone")}
                >
                  SĐT {SortIcon("phone")}
                </th>
                <th
                  className="text-left py-3 px-4 cursor-pointer hover:text-ink select-none"
                  onClick={() => handleSort("_area")}
                >
                  Khu vực {SortIcon("_area")}
                </th>
                <th
                  className="text-right py-3 px-4 text-bamboo cursor-pointer hover:text-bamboo/80 select-none whitespace-nowrap"
                  onClick={() => handleSort("_orders")}
                >
                  {timeFilter === "today" ? "Đơn Hôm Nay" :
                   timeFilter === "week" ? "Đơn Tuần Này" :
                   timeFilter === "month" ? "Đơn Tháng Này" :
                   timeFilter === "year" ? "Đơn Năm Nay" :
                   timeFilter === "custom" ? "Đơn Tùy Chọn" : "Tất cả đơn"}
                  {SortIcon("_orders")}
                </th>
                <th
                  className="text-left py-3 px-4 cursor-pointer hover:text-ink select-none whitespace-nowrap"
                  onClick={() => handleSort("last_order")}
                >
                  Lần đặt cuối {SortIcon("last_order")}
                </th>
                <th className="text-center py-3 px-4">{t("common.status")}</th>
              </tr>
            </thead>
            <tbody data-testid="care-table-body">
              {sorted.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
              ) : sorted.map((c) => (
                <React.Fragment key={c.customer_id}>
                  <tr 
                    className={cn(
                      "border-t border-border hover:bg-cream/30 cursor-pointer transition-colors", 
                      c.needs_care && "bg-amber-50/30",
                      expandedId === c.customer_id && "bg-cream/50"
                    )} 
                    onClick={() => handleExpand(c.customer_id)}
                  >
                    <td className="py-3 px-4 text-center text-ink-muted">
                      {expandedId === c.customer_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-ink-muted">
                      {c.code || c.customer_id?.slice(-6) || "—"}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      {c.name}
                      {c.nickname && <span className="text-xs text-ink-muted ml-1">({c.nickname})</span>}
                    </td>
                    <td className="py-3 px-4 text-xs">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} className="text-bamboo hover:underline flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Phone size={12} /> {c.phone}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-4 text-xs">
                      {c.district || c.city ? `${c.district || ""}${c.district && c.city ? ", " : ""}${c.city || ""}` : "—"}
                    </td>
                    
                    {/* CỘT THỐNG KÊ ĐỘNG - hiện thẳng từ periodStats */}
                    <td className="py-3 px-4 text-right text-xs">
                      {(() => {
                        const stat = periodStats[c.customer_id];
                        if (!stat) return <span className="text-ink-muted">—</span>;
                        return (
                          <div>
                            <div className="font-medium text-emerald-700">{stat.orders} đơn</div>
                            <div className="font-mono text-ink-muted">{formatVND(stat.spent)}</div>
                          </div>
                        );
                      })()}
                    </td>

                    <td className="py-3 px-4 text-xs text-ink-muted">
                      {c.last_order ? timeAgo(c.last_order, lang) : "Chưa đặt"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {!c.last_order ?
                        <Badge variant="cancelled">Chưa đặt</Badge> :
                        c.needs_care ?
                          <Badge variant="lowstock">Cần CSKH</Badge> :
                          <Badge variant="delivered">Đã đặt</Badge>}
                    </td>
                  </tr>

                  {/* DÒNG MỞ RỘNG HIỂN THỊ DANH SÁCH ĐƠN HÀNG CHI TIẾT */}
                  {expandedId === c.customer_id && (
                    <tr>
                      <td colSpan={8} className="p-0 border-b border-border bg-stone-50/50">
                        <div className="p-4 animate-fade-in border-l-4 border-bamboo">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold flex items-center gap-2">
                              <FileText size={16} className="text-ink-secondary" />
                              Danh sách đơn hàng
                              {timeFilter === "today" ? " hôm nay" : 
                               timeFilter === "week" ? " tuần này" :
                               timeFilter === "month" ? " tháng này" :
                               timeFilter === "year" ? " năm nay" : 
                               timeFilter === "custom" ? " tùy chọn" : " (Tất cả)"}
                            </h4>
                            
                            {/* TỔNG KẾT TỰ ĐỘNG BÊN TRONG BẢNG XỔ */}
                            {!loadingOrders && expandedOrders.length > 0 && (
                              <div className="bg-emerald-50 text-emerald-800 px-3 py-1 rounded text-xs font-medium border border-emerald-100">
                                Tổng: {expandedOrders.length} đơn - {formatVND(expandedTotalAmount)}
                              </div>
                            )}
                          </div>
                          
                          {loadingOrders ? (
                            <InlineRowsSkeleton rows={3} />
                          ) : expandedOrders.length === 0 ? (
                            <div className="text-center py-4 text-xs text-ink-muted italic">
                              Không có đơn hàng nào trong khoảng thời gian này.
                            </div>
                          ) : (
                            <div className="border border-border rounded overflow-hidden">
                              <table className="w-full text-xs">
                                <thead className="bg-cream/60 text-ink-muted">
                                  <tr>
                                    <th className="py-2 px-3 text-left">Mã đơn</th>
                                    <th className="py-2 px-3 text-left">Ngày đặt</th>
                                    <th className="py-2 px-3 text-right">Tổng tiền</th>
                                    <th className="py-2 px-3 text-center">Trạng thái</th>
                                    <th className="py-2 px-3 w-8"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedOrders.map((order) => (
                                    <tr
                                      key={order.order_id}
                                      className="border-t border-border bg-white hover:bg-bamboo/5 cursor-pointer transition-colors group"
                                      onClick={(e) => openOrderDetail(order.order_id, e)}
                                      title="Bấm để xem chi tiết đơn hàng"
                                    >
                                      <td className="py-2 px-3 font-mono group-hover:underline text-bamboo font-semibold">{order.order_code}</td>
                                      <td className="py-2 px-3">{order.created_at ? order.created_at.slice(0, 16).replace('T', ' ') : "—"}</td>
                                      <td className="py-2 px-3 text-right font-mono font-medium text-bamboo">{formatVND(order.total || 0)}</td>
                                      <td className="py-2 px-3 text-center">
                                        <Badge variant={order.status}>{tStatus(order.status)}</Badge>
                                      </td>
                                      <td className="py-2 px-3 text-center">
                                        <span className="inline-flex items-center text-bamboo/50 group-hover:text-bamboo transition-colors">
                                          <ChevronRight size={13} />
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {/* --- PORTAL: CHI TIẾT ĐƠN HÀNG --- */}
      {(orderDetail || orderDetailLoading) && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOrderDetail(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[85vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOrderDetail(null)}
                  className="text-ink-muted hover:text-ink mr-1 p-1 rounded-lg hover:bg-cream transition-colors"
                >
                  <ArrowLeft size={16} />
                </button>
                <div>
                  <h3 className="font-heading font-bold text-base flex items-center gap-2">
                    Đơn hàng
                    {orderDetail && <span className="font-mono text-bamboo">{orderDetail.order_code}</span>}
                  </h3>
                  {orderDetail && (
                    <p className="text-xs text-ink-muted mt-0.5">
                      {orderDetail.created_at ? orderDetail.created_at.slice(0, 10) : ""}
                      {orderDetail.assigned_shipper_name && ` · Shipper: ${orderDetail.assigned_shipper_name}`}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={() => setOrderDetail(null)} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-cream">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
              {orderDetailLoading ? (
                <div className="py-16 text-center text-sm text-ink-muted animate-pulse">Đang tải đơn hàng...</div>
              ) : orderDetail ? (
                <>
                  {/* Trạng thái + HTTT */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={orderDetail.status === "delivered" ? "delivered" : orderDetail.status === "delivering" ? "processing" : orderDetail.status === "cancelled" ? "cancelled" : "regular"}>
                      {tStatus(orderDetail.status)}
                    </Badge>
                    <Badge variant={orderDetail.payment_method === "debt" ? "lowstock" : "regular"}>
                      {{ cash: "Tiền mặt", transfer: "Chuyển khoản", ewallet: "Ví điện tử", card: "Thẻ", debt: "Công nợ" }[orderDetail.payment_method] || orderDetail.payment_method}
                    </Badge>
                    {orderDetail.delivery_shift && (
                      <Badge variant="regular">
                        {orderDetail.delivery_shift === "ca1" || orderDetail.delivery_shift === "morning" ? "☀️ Ca Sáng" : "🌤️ Ca Chiều"}
                      </Badge>
                    )}
                  </div>

                  {/* Địa chỉ giao */}
                  {(orderDetail.customer_district || orderDetail.customer_address) && (
                    <div className="flex items-start gap-2 text-sm bg-cream/30 rounded-lg px-3 py-2 border border-border/60">
                      <MapPin size={14} className="text-bamboo mt-0.5 shrink-0" />
                      <span className="text-ink-secondary">
                        {[orderDetail.customer_district, orderDetail.customer_address].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}

                  {/* Danh sách hàng hóa */}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 flex items-center gap-1">
                      <Package size={11} /> Hàng hóa
                    </div>
                    <div className="border border-border rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-cream/50 text-[11px] uppercase text-ink-muted">
                          <tr>
                            <th className="text-left py-2 px-3">Sản phẩm</th>
                            <th className="text-center py-2 px-3">Số lượng</th>
                            <th className="text-right py-2 px-3">Đơn giá</th>
                            <th className="text-right py-2 px-3">Thành tiền</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(orderDetail.items || []).map((it, i) => (
                            <tr key={i} className="border-t border-border/50 hover:bg-cream/20">
                              <td className="py-2.5 px-3 font-medium">{it.name}</td>
                              <td className="py-2.5 px-3 text-center font-mono">{it.quantity} <span className="text-xs text-ink-muted">{it.unit || ""}</span></td>
                              <td className="py-2.5 px-3 text-right font-mono text-ink-muted text-xs">{formatVND(it.unit_price || 0)}</td>
                              <td className="py-2.5 px-3 text-right font-mono font-semibold text-bamboo">{formatVND((it.unit_price || 0) * (it.quantity || 0))}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t-2 border-border bg-cream/30">
                          <tr>
                            <td colSpan={3} className="py-2.5 px-3 text-right font-semibold text-sm">TỔNG CỘNG</td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-bamboo text-base">{formatVND(orderDetail.total || 0)}</td>
                          </tr>
                          {(orderDetail.remaining_amount || 0) > 0 && (
                            <tr>
                              <td colSpan={3} className="py-1.5 px-3 text-right text-xs text-red-600 font-semibold">Còn nợ</td>
                              <td className="py-1.5 px-3 text-right font-mono font-bold text-red-600">{formatVND(orderDetail.remaining_amount)}</td>
                            </tr>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Ghi chú */}
                  {orderDetail.note && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                      <span className="font-semibold text-xs uppercase tracking-wider block mb-1">Ghi chú</span>
                      {orderDetail.note}
                    </div>
                  )}

                  {/* Timeline */}
                  {(orderDetail.timeline || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 flex items-center gap-1">
                        <Clock size={11} /> Lịch sử trạng thái
                      </div>
                      <div className="space-y-2">
                        {[...(orderDetail.timeline || [])].reverse().map((ev, i) => (
                          <div key={i} className="flex gap-3 text-xs">
                            <div className="w-1.5 h-1.5 rounded-full bg-bamboo mt-1.5 shrink-0" />
                            <div>
                              <span className="font-semibold text-ink">{tStatus(ev.status)}</span>
                              {ev.note && <span className="text-ink-muted ml-1">— {ev.note}</span>}
                              <div className="text-ink-muted mt-0.5">
                                {ev.at ? (typeof ev.at === "string" ? ev.at.slice(0, 16).replace("T", " ") : "") : ""}
                                {ev.by && ` · ${ev.by}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}